import { MessageChatCircle } from "@untitledui/icons";
import type { RiskState } from "@/components/safety-config/RiskRow";
import { GROUP_ICONS, type RiskGroup, type RiskItem } from "@/components/safety-config/risk-groups";

type RiskSummaryBandProps = {
  groups: RiskGroup[];
  getRiskState: (item: RiskItem) => RiskState;
};

export const RiskSummaryBand = ({ groups, getRiskState }: RiskSummaryBandProps) => {
  const totalActive = groups.reduce((s, g) => s + g.items.filter((i) => getRiskState(i).enabled).length, 0);
  const totalAll = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="flex items-center gap-6 pt-6 px-1.5 justify-between">
      {/* Total block */}
      <div className="flex flex-col gap-1.5 shrink-0 min-w-[140px]">
        <div className="flex items-end gap-1">
          <span className="text-3xl font-medium text-primary leading-none">{totalActive}</span>
          <span className="text-xl text-quaternary leading-none">of {totalAll}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-quaternary">
          <span className="w-1.5 h-1.5 rounded-sm bg-success-500 shrink-0" />
          Risks being monitored
        </div>
      </div>

      <div className="w-px self-stretch bg-border-primary shrink-0" />

      {/* Groups grid — fills column-major (top-to-bottom, then next column) so items read 1/2, 3/4, etc. */}
      <div className="grid grid-flow-col grid-rows-2 gap-x-5 gap-y-1.5 flex-1 min-w-0 max-w-[740px]">
        {groups.map((g) => {
          const active = g.items.filter((i) => getRiskState(i).enabled).length;
          const Icon = GROUP_ICONS[g.id] ?? MessageChatCircle;
          return (
            <div key={g.id} className="flex items-center gap-1.5 text-sm text-quaternary whitespace-nowrap">
              <span className="text-base font-medium text-primary">{active}</span>
              <Icon aria-hidden="true" className="w-3.5 h-3.5 text-black shrink-0" />
              <span className="truncate">{g.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
